import { FontFamily } from '@euler/components/typography';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { memo, ReactNode, useMemo } from 'react';
import {
  ActivityIndicator,
  StyleProp,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
  ViewStyle,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useTheme } from 'styled-components';

type Props = {
  isLoading?: boolean;
  error?: Error;
  hasMore?: boolean;
  style?: StyleProp<ViewStyle>;
  noMoreText?: string;
  total: number;
  space?: number;
  onRetry?: () => void;
};

const ContentContainer = memo(
  ({
    style,
    children,
  }: {
    style?: StyleProp<ViewStyle>;
    children?: ReactNode;
  }) => {
    const styles = useStyles();
    return (
      <View style={[styles.contentContainer, style]}>
        <View style={styles.contentWrapper}>{children}</View>
      </View>
    );
  },
);

export const FooterView = memo(
  ({ isLoading, total, error, hasMore, style, noMoreText, onRetry }: Props) => {
    const theme = useTheme();
    const styles = useStyles();

    if (isLoading) {
      return (
        <ContentContainer style={style}>
          <ActivityIndicator
            color={theme.components.scrollView.activityIndicator.color}
          />
        </ContentContainer>
      );
    }

    if (total) {
      if (error) {
        return (
          <ContentContainer style={style}>
            <View
              css={`
                flex-direction: row;
                justify-content: center;
                align-items: center;
              `}
            >
              <Text
                style={styles.footerText}
                css={`
                  color: ${theme.colors.primary};
                `}
              >
                加载数据失败，
              </Text>
              <TouchableOpacity onPress={onRetry}>
                <Text
                  style={styles.footerText}
                  css={`
                    color: ${theme.colors.primary};
                  `}
                >
                  重试
                </Text>
              </TouchableOpacity>
            </View>
          </ContentContainer>
        );
      }

      if (!hasMore) {
        return (
          <ContentContainer style={style}>
            <Text
              style={styles.footerText}
              css={`
                color: ${theme.components.scrollView.activityIndicator.color};
                text-align: center;
              `}
            >
              {noMoreText ?? `没有更多了`}
            </Text>
          </ContentContainer>
        );
      }
    }

    return <ContentContainer style={style} />;
  },
);

const useStyles = () => {
  const { rpx } = useResponsiveContext();
  const { bottom } = useSafeAreaInsets();

  return useMemo(
    () =>
      StyleSheet.create({
        contentContainer: {
          paddingTop: rpx(8),
          paddingBottom: rpx(8) + bottom,
        },
        // fix the height of the footer content, otherwise, the scroll view
        // will keep firing the end reached event once the height changes.
        contentWrapper: {
          height: rpx(24),
          justifyContent: 'center',
          alignItems: 'center',
        },
        footerText: {
          fontFamily: FontFamily.NotoSans.Light,
          fontSize: rpx(11),
          lineHeight: rpx(13),
        },
      }),
    [bottom, rpx],
  );
};
